import React, { useRef } from 'react';

import { CreditLimitBannerDisplayIn } from '@repo/editor/src/components/CreditsLimitBanner/CreditsLimitBanner';
import {
  authenticationStoreSelector,
  useAuthenticationStore,
} from '@repo/editor/src/stores/authenticationStore';
import {
  promotionSelector,
  usePromotionStore,
} from '@repo/editor/src/stores/promotionStore';
import {
  userStoreSelector,
  useUserStore,
} from '@repo/editor/src/stores/userStore';
import { userHasEnoughCredits } from '@repo/editor/src/utils/credits';
import { withEventScope } from '@repo/event-processor';
import { Spacer } from '@repo/ui/components/Spacer';

import {
  useVectorizerStore,
  vectorizerStoreSelector,
} from 'stores/vectorizerStore';
import { FlexColumn } from 'styles/layouts';

import FilePicker from '../../AiTools/FilePicker/FilePicker';
import UpgradeNotificationBox from '../../AiTools/UpgradeNotificatioBox/UpgradeNotificationBox';
import Settings, { VectorizerOptions } from '../Settings/Settings';
import SourceTemplate from '../SourceTemplate/SourceTemplate';

import { SourceTemplateWrapper, Wrapper } from './styles';

const VectorizerTool: React.FC = () => {
  const loadingUser = useUserStore(userStoreSelector.loadingUser);

  const userPlan = useUserStore(userStoreSelector.user)?.plan;

  const showUpgradeModal = usePromotionStore(
    promotionSelector.showUpgradeModal
  );

  const loading = useVectorizerStore(vectorizerStoreSelector.loading);
  const vectorizing = useVectorizerStore(vectorizerStoreSelector.vectorizing);
  const image = useVectorizerStore(vectorizerStoreSelector.image);
  const displayedImage = useVectorizerStore(
    vectorizerStoreSelector.displayedImage
  );
  const vectorized = useVectorizerStore(vectorizerStoreSelector.vectorized);
  const actions = useVectorizerStore(vectorizerStoreSelector.actions);

  const updateUserInfo = useUserStore(userStoreSelector.updateUserInfo);
  const hasCredits = userHasEnoughCredits(loadingUser || null);
  const proAccess = useUserStore(userStoreSelector.proAccess);

  const openAuthentication = useAuthenticationStore(
    authenticationStoreSelector.openAuthentication
  );

  const sourceTemplateRef = useRef<HTMLDivElement>(null);

  const handleDisabledClick = (): void => {
    if (!loadingUser) {
      openAuthentication({
        blurBackground: true,
      });

      return;
    }

    if (!hasCredits) {
      showUpgradeModal('standAlonePagesTools');
    }
  };

  const handleVectorize = async (
    options: VectorizerOptions,
    colorCounter: number
  ): Promise<void> => {
    if (!hasCredits && !proAccess) {
      showUpgradeModal('standAlonePagesTools');
      return;
    }
    await actions.vectorize(options, colorCounter);

    const userInfo = await updateUserInfo();
    if (userInfo && !hasCredits && !userInfo.plan) {
      showUpgradeModal('standAlonePagesTools');
    }

    sourceTemplateRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  const vectorizingAvailable =
    !!loadingUser && !loading && (proAccess || hasCredits);

  return (
    <FlexColumn width="100%">
      {(!loadingUser || !loadingUser.plan) && (
        <UpgradeNotificationBox
          displayIn={CreditLimitBannerDisplayIn.SVG_CONVERTER_WEBSITE}
          upgradeModalType="standAlonePagesTools"
        />
      )}
      <Spacer height="24px" />
      <Wrapper fullHeight={vectorized}>
        <SourceTemplateWrapper ref={sourceTemplateRef}>
          {image ? (
            <SourceTemplate
              background="/images/bgRemoverTransparent.png"
              imageSrc={displayedImage ?? image}
              isLoading={loadingUser === false || loading}
              isModified={!!displayedImage}
              stage={vectorized ? 'vectorizing' : 'removingBackground'}
              vectorizing={vectorizing}
              onDownload={actions.download}
              onRemoveBackground={
                !displayedImage ? actions.removeBackground : undefined
              }
              onReplace={actions.selectFile}
              onReset={actions.reset}
            />
          ) : (
            <FilePicker
              disabled={!vectorizingAvailable}
              onDisabledClick={handleDisabledClick}
              onFileSelect={actions.selectFile}
            />
          )}
        </SourceTemplateWrapper>
        {!vectorized && !vectorizing && (
          <div>
            <Settings
              disabled={!vectorizingAvailable || !image}
              isLoading={vectorizing}
              userPlan={userPlan}
              onVectorizeClick={handleVectorize}
            />
          </div>
        )}
      </Wrapper>
    </FlexColumn>
  );
};

export default withEventScope(VectorizerTool, { module: 'vectorizerTool' });
